import React, { useState } from "react";
import { message } from "antd";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Fade,
  Slide,
  Switch,
} from "@mui/material";
import { API_BASE_URL, getAuthHeaders, getUploadAuthHeaders } from "../API/Api";

const PostPromotion = () => {
  const [title, setTitle] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [imageUrl, setImageUrl] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const handleReset = () => {
    setTitle("");
    setIsActive(true);
    setImageUrl("");
    setPreviewImageUrl("");
    setPdfUrl("");
    setPreviewPdfUrl("");
  };

  const handleImageUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setPreviewImageUrl(localPreview);
    setUploadingImage(true);

    let remotePreviewUrl = null;

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("image", file);

      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        headers: getUploadAuthHeaders(),
        body: uploadFormData,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const uploadedPath =
        uploadData?.image_path ||
        uploadData?.path ||
        uploadData?.data?.path ||
        uploadData?.url ||
        "";

      if (!uploadedPath) {
        throw new Error("Upload response missing file path");
      }

      remotePreviewUrl =
        uploadData?.url ||
        (String(uploadedPath).startsWith("http") ? uploadedPath : null);

      setImageUrl(uploadedPath);
      if (remotePreviewUrl) setPreviewImageUrl(remotePreviewUrl);
      // message.success("Image uploaded");
    } catch (error) {
      console.error("Image upload failed:", error);
      // message.error("Failed to upload image");
      setImageUrl("");
      setPreviewImageUrl("");
      URL.revokeObjectURL(localPreview);
    } finally {
      if (remotePreviewUrl) URL.revokeObjectURL(localPreview);
      setUploadingImage(false);
    }
  };

  const handlePdfUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setPreviewPdfUrl(localPreview);
    setUploadingPdf(true);

    try {
      console.log("PDF file selected:", {
        name: file?.name,
        size: file?.size,
        type: file?.type,
        lastModified: file?.lastModified,
      });

      // Using the existing image upload endpoint. We send the file under
      // both `image` and `pdf` keys to match potential backend expectations.
      const uploadFormData = new FormData();
      uploadFormData.append("image", file);
      uploadFormData.append("pdf", file);

      console.log("Uploading PDF to:", `${API_BASE_URL}/admin/images/upload`);

      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        headers: getUploadAuthHeaders(),
        body: uploadFormData,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      console.log("PDF upload response:", uploadData);
      const uploadedPath =
        uploadData?.pdf_path ||
        uploadData?.image_path ||
        uploadData?.path ||
        uploadData?.data?.path ||
        uploadData?.url ||
        "";

      if (!uploadedPath) {
        throw new Error("Upload response missing file path");
      }

      setPdfUrl(uploadedPath);
      console.log("PDF uploadedPath:", uploadedPath);
      if (uploadData?.url) setPreviewPdfUrl(uploadData.url);
      // message.success("PDF uploaded");
    } catch (error) {
      console.error("PDF upload failed:", error);
      // message.error("Failed to upload PDF");
      setPdfUrl("");
      setPreviewPdfUrl("");
    } finally {
      URL.revokeObjectURL(localPreview);
      setUploadingPdf(false);
    }
  };

  const onChangeImageUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) handleImageUpload(file);
  };

  const onChangePdfUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) handlePdfUpload(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      message.warning("Please enter promotion title");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        is_active: isActive,
        image: imageUrl || null,
        pdf_url: pdfUrl || null,
      };

      console.log("Creating promotion with pdf:", payload.pdf);
      const res = await fetch(`${API_BASE_URL}/admin/promotions`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      message.success("Promotion created successfully");
      window.location.href = "/admin/promotions";
    } catch (error) {
      console.error("Error creating promotion:", error);
      message.error("Failed to create promotion");
    } finally {
      setSubmitting(false);
    }
  };

  const pdfHref =
    (previewPdfUrl || pdfUrl) &&
    String(previewPdfUrl || pdfUrl).startsWith("http")
      ? previewPdfUrl || pdfUrl
      : null;

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1000, mx: "auto", my: 5, px: 2 }}>
        <Slide direction="up" in timeout={400}>
          <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Box component="form" onSubmit={onSubmit}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Create Promotion
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                      textAlign: "center",
                      backgroundColor: "grey.50",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Promotion Image
                    </Typography>
                    <Box
                      sx={{
                        width: 180,
                        height: 180,
                        mx: "auto",
                        mb: 2,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "common.white",
                      }}
                    >
                      {previewImageUrl || imageUrl ? (
                        <Box
                          component="img"
                          src={previewImageUrl || imageUrl}
                          alt="Promotion preview"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </Box>

                    <input
                      id="promotion-image-upload"
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={onChangeImageUpload}
                    />
                    <label htmlFor="promotion-image-upload">
                      <Button variant="outlined" component="span">
                        {uploadingImage ? "Uploading..." : "Upload Image"}
                      </Button>
                    </label>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                      textAlign: "center",
                      backgroundColor: "grey.50",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Promotion PDF
                    </Typography>

                    <Box
                      sx={{
                        width: "100%",
                        mx: "auto",
                        mb: 2,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "common.white",
                        minHeight: 180,
                        p: 2,
                      }}
                    >
                      {pdfHref ? (
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            Selected PDF
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <a href={pdfHref} target="_blank" rel="noreferrer">
                              <Button size="small" variant="outlined">
                                View
                              </Button>
                            </a>
                          </Box>
                        </Box>
                      ) : pdfUrl ? (
                        <Typography variant="body2" color="text.secondary">
                          PDF uploaded (preview unavailable)
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No PDF
                        </Typography>
                      )}
                    </Box>

                    <input
                      id="promotion-pdf-upload"
                      type="file"
                      hidden
                      accept="application/pdf"
                      onChange={onChangePdfUpload}
                    />
                    <label htmlFor="promotion-pdf-upload">
                      <Button variant="outlined" component="span">
                        {uploadingPdf ? "Uploading..." : "Upload PDF"}
                      </Button>
                    </label>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Active
                  </Typography>
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button variant="outlined" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        (window.location.href = "/admin/promotions")
                      }
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
};

export default PostPromotion;
